import type { LucideIcon } from 'lucide-react'
import { Link, useLocation } from 'react-router-dom'
import {
  ArrowLeft,
  ArrowRight,
  Briefcase,
  CalendarDays,
  CheckCircle2,
  ClipboardList,
  CreditCard,
  FileText,
  Landmark,
  ListChecks,
  Receipt,
  Users,
  Wallet,
} from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { getServiceInstanceIdFromSearch, withServiceInstanceId } from '@/lib/service-instance'
import { useWorkspaceLabels } from '@/contexts/workspace-portal-context'
import { cn } from '@/lib/utils'

type LifecycleStage = {
  id: string
  step: number
  title: string
  summary: string
  icon: LucideIcon
  tone: string
  iconTone: string
  primaryPath: string
  primaryLabel: string
  outputs?: string[]
}

const buildLifecycleStages = (labels: ReturnType<typeof import('@/lib/workspace-labels').getWorkspaceLabels>): LifecycleStage[] => [
  {
    id: 'create',
    step: 1,
    title: labels.mode === 'project' ? 'Create project' : labels.mode === 'event' ? 'Create event' : 'Create job',
    summary: labels.mode === 'project' ? 'New project in draft or scheduled state.' : labels.mode === 'event' ? 'New event in draft or scheduled state.' : 'New job in draft or scheduled state.',
    icon: Briefcase,
    tone: 'border-sky-200 bg-sky-50',
    iconTone: 'bg-sky-100 text-sky-700',
    primaryPath: `${labels.basePath}/create`,
    primaryLabel: labels.mode === 'project' ? 'Create project' : labels.mode === 'event' ? 'Create event' : 'Create job',
  },
  {
    id: 'assign',
    step: 2,
    title: 'Assign workers',
    summary: `Link workers, roles, and pay to the ${labels.entityLower}.`,
    icon: Users,
    tone: 'border-violet-200 bg-violet-50',
    iconTone: 'bg-violet-100 text-violet-700',
    primaryPath: labels.mode === 'event' ? '/events/assignments' : labels.mode === 'project' ? '/projects/assignments' : '/assignments',
    primaryLabel: 'Assignments',
    outputs: ['Mark each assignment Completed when work is done'],
  },
  {
    id: 'workdays',
    step: 3,
    title: labels.workDayPlural,
    summary: `Schedule shifts, log hours, and approve ${labels.workDayEntryPluralLower}.`,
    icon: CalendarDays,
    tone: 'border-rose-200 bg-rose-50',
    iconTone: 'bg-rose-100 text-rose-700',
    primaryPath: labels.workDaysBasePath,
    primaryLabel: labels.workDayPlural,
    outputs: [`Every ${labels.workDayEntryLower} must be Approved`],
  },
  {
    id: 'costs',
    step: 4,
    title: 'Costs & checklist',
    summary: 'Billable expenses, inventory, required steps.',
    icon: ListChecks,
    tone: 'border-amber-200 bg-amber-50',
    iconTone: 'bg-amber-100 text-amber-700',
    primaryPath: '/job/expenses',
    primaryLabel: 'Expenses',
  },
  {
    id: 'preview',
    step: 5,
    title: 'Invoice preview',
    summary: 'Optional quotation before completion.',
    icon: FileText,
    tone: 'border-slate-200 bg-slate-50',
    iconTone: 'bg-slate-100 text-slate-700',
    primaryPath: '/finance/invoices',
    primaryLabel: 'Invoices',
  },
  {
    id: 'complete',
    step: 6,
    title: labels.mode === 'project' ? 'Complete project' : labels.mode === 'event' ? 'Complete event' : 'Complete job',
    summary: 'Status → Completed triggers invoices.',
    icon: CheckCircle2,
    tone: 'border-emerald-200 bg-emerald-50',
    iconTone: 'bg-emerald-100 text-emerald-700',
    primaryPath: labels.basePath,
    primaryLabel: `${labels.entityPlural} list`,
    outputs: [
      'All assignments Completed',
      `All ${labels.workDayEntryPluralLower} Approved`,
      'Client invoice issued',
      'Worker payout invoices created',
    ],
  },
  {
    id: 'pay',
    step: 7,
    title: 'Record payments',
    summary: 'Pay workers and log client receipts.',
    icon: Wallet,
    tone: 'border-blue-200 bg-blue-50',
    iconTone: 'bg-blue-100 text-blue-700',
    primaryPath: '/job/worker/payments',
    primaryLabel: 'Worker payouts',
  },
]

const JOB_STATUS_FLOW = [
  'Draft',
  'Open',
  'In progress',
  'On hold',
  'Completed',
  'Cancelled',
]

type InvoiceOutcome = {
  label: string
  sub: string
  icon: LucideIcon
  path: string
  auto: boolean
}

const INVOICE_OUTCOMES: InvoiceOutcome[] = [
  {
    label: 'Client invoice',
    sub: 'Bill the customer',
    icon: FileText,
    path: '/finance/invoices',
    auto: true,
  },
  {
    label: 'Worker payout',
    sub: 'Pay each assigned worker',
    icon: Wallet,
    path: '/job/worker/payments',
    auto: true,
  },
  {
    label: 'Client payment',
    sub: 'When customer pays',
    icon: Landmark,
    path: '/finance/invoice-payments',
    auto: false,
  },
  {
    label: 'Cash to company',
    sub: 'Worker returns collected cash',
    icon: CreditCard,
    path: '/job/company/payments',
    auto: false,
  },
]

function StageArrow() {
  return (
    <div className="flex shrink-0 items-center justify-center px-1 sm:px-2" aria-hidden>
      <ArrowRight className="h-4 w-4 text-muted-foreground/50 sm:h-5 sm:w-5" />
    </div>
  )
}

function LifecycleStageCard({
  stage,
  href,
}: {
  stage: LifecycleStage
  href: string
}) {
  const Icon = stage.icon

  return (
    <Link
      to={href}
      className={cn(
        'group flex min-w-[148px] max-w-[168px] shrink-0 flex-col rounded-xl border p-3 shadow-sm transition-all hover:shadow-md sm:min-w-[160px]',
        stage.tone,
      )}
    >
      <div className="mb-2 flex items-center justify-between gap-1">
        <span
          className={cn(
            'flex h-7 w-7 items-center justify-center rounded-lg',
            stage.iconTone,
          )}
        >
          <Icon className="h-3.5 w-3.5" />
        </span>
        <Badge variant="outline" className="h-5 px-1.5 text-[10px] font-normal">
          {stage.step}
        </Badge>
      </div>
      <p className="text-xs font-semibold leading-tight text-foreground group-hover:text-primary">
        {stage.title}
      </p>
      <p className="mt-1 flex-1 text-[10px] leading-snug text-muted-foreground">
        {stage.summary}
      </p>
      {stage.outputs?.length ? (
        <ul className="mt-2 space-y-0.5 border-t border-border/40 pt-2">
          {stage.outputs.map((line) => (
            <li key={line} className="text-[9px] leading-snug text-muted-foreground">
              · {line}
            </li>
          ))}
        </ul>
      ) : null}
      <span className="mt-2 inline-flex items-center gap-0.5 text-[10px] font-medium text-primary">
        {stage.primaryLabel}
        <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
      </span>
    </Link>
  )
}

export default function JobLifecyclePage() {
  const location = useLocation()
  const serviceInstanceId = getServiceInstanceIdFromSearch(location.search)
  const labels = useWorkspaceLabels()
  const jobsHref = withServiceInstanceId(labels.basePath, serviceInstanceId)
  const lifecycleStages = buildLifecycleStages(labels)

  return (
    <div className="space-y-6 pb-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-2">
          <Button variant="ghost" size="sm" className="-ml-2 h-8 px-2 text-xs" asChild>
            <Link to={jobsHref}>
              <ArrowLeft className="mr-1.5 h-3.5 w-3.5" />
              {`Back to ${labels.entityPlural.toLowerCase()}`}
            </Link>
          </Button>
          <div>
            <Badge variant="outline" className="mb-2 border-blue-200 bg-blue-50 text-blue-700">
              Left → right
            </Badge>
            <h1 className="text-2xl font-bold tracking-tight text-foreground">{`${labels.entity} lifecycle`}</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {`Follow the pipeline from ${labels.entityLower} creation through completion and finance. Scroll horizontally on smaller screens.`}
            </p>
          </div>
        </div>
        <Button size="sm" className="shrink-0 rounded-lg" asChild>
          <Link to={withServiceInstanceId(`${labels.basePath}/create`, serviceInstanceId)}>
            <Briefcase className="mr-2 h-4 w-4" />
            {`Create ${labels.entityLower}`}
          </Link>
        </Button>
      </div>

      <Card className="overflow-hidden rounded-2xl border-border/70">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Lifecycle pipeline</CardTitle>
          <CardDescription>Each stage links to the main screen for that step.</CardDescription>
        </CardHeader>
        <CardContent className="pb-4">
          <div className="-mx-1 overflow-x-auto px-1 pb-2">
            <div className="flex min-w-max items-stretch py-2">
              {lifecycleStages.map((stage, index) => (
                <div key={stage.id} className="flex items-stretch">
                  <LifecycleStageCard
                    stage={stage}
                    href={withServiceInstanceId(stage.primaryPath, serviceInstanceId)}
                  />
                  {index < lifecycleStages.length - 1 ? <StageArrow /> : null}
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-2xl border-border/70">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">{labels.entity} status progression</CardTitle>
          <CardDescription>{`How ${labels.entityLower} status moves while work is underway.`}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="-mx-1 overflow-x-auto px-1">
            <div className="flex min-w-max items-center gap-2 py-1">
              {JOB_STATUS_FLOW.map((label, index) => (
                <div key={label} className="flex items-center gap-2">
                  <Badge
                    variant="outline"
                    className={cn(
                      'shrink-0 rounded-md px-2.5 py-1 text-xs font-normal',
                      label === 'Completed' && 'border-emerald-300 bg-emerald-50 text-emerald-800',
                      label === 'Cancelled' && 'border-rose-200 bg-rose-50 text-rose-700',
                    )}
                  >
                    {label}
                  </Badge>
                  {index < JOB_STATUS_FLOW.length - 1 ? (
                    <ArrowRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground/45" />
                  ) : null}
                </div>
              ))}
            </div>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            <strong className="font-medium text-foreground">Completed</strong> requires the checklist,
            every {labels.entityLower} assignment in Completed status, and every {labels.workDayEntryLower} Approved.
          </p>
        </CardContent>
      </Card>

      <Card className="overflow-hidden rounded-2xl border-emerald-200/70 bg-emerald-50/30">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <Receipt className="h-4 w-4 text-emerald-700" />
            At completion → invoices & payments
          </CardTitle>
          <CardDescription>
            {`Auto-generated when the ${labels.entityLower} is marked Completed, then manual payment recording.`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="-mx-1 overflow-x-auto px-1 pb-1">
            <div className="flex min-w-max items-stretch gap-3 py-1">
              {INVOICE_OUTCOMES.map((item) => {
                const Icon = item.icon
                return (
                  <Link
                    key={item.label}
                    to={withServiceInstanceId(item.path, serviceInstanceId)}
                    className="group flex min-w-[140px] shrink-0 flex-col rounded-lg border border-border/60 bg-background/90 p-3 shadow-sm transition hover:border-primary/30 hover:shadow"
                  >
                    <div className="mb-2 flex items-center justify-between gap-2">
                      <span className="flex h-7 w-7 items-center justify-center rounded-md bg-muted">
                        <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                      </span>
                      <Badge
                        variant="outline"
                        className={cn(
                          'h-5 px-1.5 text-[9px] font-normal',
                          item.auto
                            ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                            : 'border-slate-200 bg-slate-50 text-slate-600',
                        )}
                      >
                        {item.auto ? 'Auto' : 'Manual'}
                      </Badge>
                    </div>
                    <p className="text-xs font-semibold text-foreground group-hover:text-primary">
                      {item.label}
                    </p>
                    <p className="mt-0.5 text-[10px] text-muted-foreground">{item.sub}</p>
                  </Link>
                )
              })}
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex flex-wrap gap-2">
        <Button variant="outline" size="sm" asChild>
          <Link to={jobsHref}>
            <ClipboardList className="mr-2 h-4 w-4" />
            {labels.entityPlural}
          </Link>
        </Button>
        <Button variant="outline" size="sm" asChild>
          <Link to={withServiceInstanceId(labels.mode === 'event' ? '/events/assignments' : labels.mode === 'project' ? '/projects/assignments' : '/assignments', serviceInstanceId)}>
            <Users className="mr-2 h-4 w-4" />
            Assignments
          </Link>
        </Button>
        <Button variant="outline" size="sm" asChild>
          <Link to={withServiceInstanceId(labels.workDaysBasePath, serviceInstanceId)}>
            <CalendarDays className="mr-2 h-4 w-4" />
            {labels.workDayPlural}
          </Link>
        </Button>
        <Button variant="outline" size="sm" asChild>
          <Link to={withServiceInstanceId(`${labels.basePath}/checklists`, serviceInstanceId)}>
            <ListChecks className="mr-2 h-4 w-4" />
            Checklists
          </Link>
        </Button>
      </div>
    </div>
  )
}
